import { formatPatronListIdLine } from './patronIdentity'

/**
 * Derive pipeline-style stage id from a raw /api/cases row (aligned with InvestigationPipeline mapApiCaseToPipeline
 * and backend/src/utils/pipelineStageMap.js).
 */
// Pipeline starts at Investigation — alerts live in Monitor Safe
const STATUS_TO_STAGE = {
  OPEN: 'investigation',
  INVESTIGATING: 'investigation',
  PENDING_EVIDENCE: 'pending_evidence',
  SUSPICION_FORMED: 'suspicion_formed',
  SMR_DRAFT: 'smr_draft',
  ESCALATED: 'investigation',
  // PENDING_REVIEW = CO / compliance drafting (legacy + seeds); manager queue = PENDING_APPROVAL
  PENDING_REVIEW: 'smr_draft',
  PENDING_APPROVAL: 'under_review',
  FILED: 'filed',
  RESOLVED: 'closed',
  CLOSED: 'closed',
}

/**
 * Resolve Kanban column for a case row (list or detail).
 * @param {Record<string, unknown>} apiCase
 * @returns {string} stage id (e.g. smr_draft, under_review)
 */
export function resolvePipelineStage(apiCase) {
  const status = String(apiCase?.status || 'INVESTIGATING').toUpperCase()
  return STATUS_TO_STAGE[status] || 'investigation'
}

export const WORKSPACE_STAGE_LABELS = {
  investigation: 'Investigation',
  pending_evidence: 'Pending evidence',
  suspicion_formed: 'Suspicion formed',
  smr_draft: 'SMR Draft',
  under_review: 'Under review',
  filed: 'Filed',
  closed: 'Closed',
}

/**
 * @param {string} stageId - pipeline stage key (e.g. triage, smr_draft)
 * @param {Record<string, string>} customLabels - from configuration INVESTIGATION_STAGE_LABELS (same as Investigation Pipeline)
 */
export function resolveStageLabel(stageId, customLabels = {}) {
  const id = String(stageId || '')
  const custom = customLabels && typeof customLabels === 'object' ? customLabels[id] : null
  if (typeof custom === 'string' && custom.trim()) return custom.trim()
  return WORKSPACE_STAGE_LABELS[id] || id
}

export function getWorkspaceStageId(apiCase) {
  return resolvePipelineStage(apiCase)
}

export function getWorkspaceStageLabel(apiCase, customLabels = {}) {
  return resolveStageLabel(getWorkspaceStageId(apiCase), customLabels)
}

/** Shown when the case has a member id but no resolved name — member id stays on the second line. */
export const PATRON_NAME_PLACEHOLDER = 'Unnamed patron'

/** Visitor / uncarded payout — paired with case_number so multiple rows stay distinguishable. */
export const UNCARDED_LABEL = 'Uncarded'

/** Collapse titles like "John Smith · John Smith" (seed/API quirks) to a single label. */
export function dedupePatronTitleLabel(raw) {
  if (raw == null) return ''
  let t = String(raw).trim()
  if (!t) return ''
  const split = t.split(/\s*[·•|,]\s*/).map((s) => s.trim()).filter(Boolean)
  if (split.length >= 2 && split.every((p) => p === split[0])) return split[0]
  return t
}

/** First + last from watchlist overview `profile` (member_directory-backed). */
export function patronDirectoryFullName(profile) {
  if (!profile || typeof profile !== 'object') return ''
  return [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim()
}

/**
 * Investigation UI: prefer the name recorded on the case / payout snapshot over the live profile.
 * Profile name is shown separately when it differs (identity review).
 */
export function resolveCasePatronDisplayName(profile, caseData) {
  const fromCase = resolvePatronDisplayName(caseData?.member_name)
  const fromSnap = resolveNameFromSnapshot(caseData)
  const dir = patronDirectoryFullName(profile)
  const mid = caseData?.member_id
  return dedupePatronTitleLabel(
    fromCase || fromSnap || dir || (mid ? `Member ${mid}` : 'Unidentified')
  )
}

/** @returns {string|null} Trimmed display name, or null if missing / N/A. */
export function resolvePatronDisplayName(memberName) {
  const n = memberName != null ? String(memberName).trim() : ''
  if (!n || n === 'N/A') return null
  return dedupePatronTitleLabel(n)
}

export function parseCaseSnapshot(apiCase) {
  const raw = apiCase?.source_data_snapshot
  if (raw == null) return {}
  if (typeof raw === 'object') return raw
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return {}
}

export function hasCaseMemberId(apiCase) {
  const mid = apiCase?.member_id
  if (mid == null) return false
  const s = String(mid).trim()
  if (!s || s.toUpperCase() === 'UNKNOWN') return false
  return true
}

/**
 * At most one investigation row per carded member_id. Order is preserved: caller should sort
 * first (e.g. newest first) so the kept row is the preferred one. Rows without a real member_id
 * (uncarded / unidentified) are all kept.
 */
const TERMINAL_CASE_STATUSES = new Set(['CLOSED', 'RESOLVED', 'FILED'])

export function dedupeInvestigationCasesOnePerMemberId(orderedCases) {
  if (!Array.isArray(orderedCases)) return []
  const seen = new Set()
  const out = []
  for (const c of orderedCases) {
    if (!hasCaseMemberId(c)) {
      out.push(c)
      continue
    }
    const key = String(c.member_id).trim()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(c)
  }
  return out
}

/**
 * Pipeline Kanban: show every open investigation per member (PSDEV-303 / PSDEV-702).
 * Caller should pass cases sorted newest-first. For terminal cases only, keep the newest per member.
 */
export function dedupeInvestigationCasesForPipeline(orderedCases) {
  if (!Array.isArray(orderedCases)) return []
  const noMember = []
  const activeByMember = new Map()
  const terminalNewest = new Map()

  for (const c of orderedCases) {
    if (!hasCaseMemberId(c)) {
      noMember.push(c)
      continue
    }
    const key = String(c.member_id).trim()
    const status = String(c.status || '').toUpperCase()
    if (!TERMINAL_CASE_STATUSES.has(status)) {
      const arr = activeByMember.get(key) || []
      arr.push(c)
      activeByMember.set(key, arr)
    } else if (!terminalNewest.has(key)) {
      terminalNewest.set(key, c)
    }
  }

  const out = [...noMember]
  for (const arr of activeByMember.values()) out.push(...arr)
  for (const c of terminalNewest.values()) out.push(c)
  return out.sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  )
}

/** Visitor id from case snapshot (payout / tablet paths). */
export function snapshotVisitorId(apiCase) {
  const snap = parseCaseSnapshot(apiCase)
  const v = snap.VisitorID ?? snap.visitor_id ?? snap.visitorId ?? snap.VisitorId
  if (v == null || String(v).trim() === '') return null
  return String(v).trim()
}

/** Name from snapshot when member_name column is empty (mirrors backend enrichment). */
export function resolveNameFromSnapshot(apiCase) {
  const snap = parseCaseSnapshot(apiCase)
  const n =
    snap.MemberName ||
    snap.memberName ||
    snap.member_name ||
    snap.VisitorName ||
    snap.visitor_name ||
    snap.visitorName
  if (n == null) return null
  const t = String(n).trim()
  return t && t !== 'N/A' ? t : null
}

/**
 * Primary title for investigation list / pipeline cards.
 * Order: member_name → snapshot (member or visitor name) → Uncarded · CASE-REF (no member id) → Unnamed patron.
 */
export function resolvePatronListTitle(apiCase) {
  const fromCol = resolvePatronDisplayName(apiCase?.member_name)
  if (fromCol) return fromCol
  const fromSnap = resolveNameFromSnapshot(apiCase)
  if (fromSnap) return dedupePatronTitleLabel(fromSnap)
  const caseRef = String(apiCase?.case_number || apiCase?.id || '').trim() || '—'
  if (!hasCaseMemberId(apiCase)) {
    return `${UNCARDED_LABEL} · ${caseRef}`
  }
  return PATRON_NAME_PLACEHOLDER
}

/** Second line: patron / master profile id, with member or visitor id when known. */
export function resolvePatronListSecondaryId(apiCase) {
  const fromIdentity = formatPatronListIdLine(apiCase)
  if (fromIdentity) return fromIdentity
  if (hasCaseMemberId(apiCase)) return String(apiCase.member_id).trim()
  const vid = snapshotVisitorId(apiCase)
  if (vid) return `Visitor ${vid}`
  return ''
}
